import { useQuery } from "@apollo/client";
import { CompanyMatchStatusPayload } from "../types/companyMatch";
import { COMPANY_MATCH_FILE_STATS } from "../apis/companyMatch/queries";
import { useSnackbar } from "../components/Snackbar";
import { COMPANY_MATCH_CLIENT } from "../utils/consts";
import { useWorkspaceStore } from "../store/workspace/WorkspaceStore";
import {
  useMatchDataProcessingStore,
  MatchDataProcessingType,
} from "../store/MatchDataProcessingStore";
import { Workspace } from "../types/workspace";

// Polling interval in milliseconds (10 seconds)
const POLLING_INTERVAL_MS = 1000 * 10;

interface UseMatchDataPollingOptions {
  type: MatchDataProcessingType;
}

/**
 * Generic hook to handle match data file processing and polling
 * It checks localStorage for any pending processing files for the current workspace
 * and sets up polling to the backend until processing is complete
 */
export const useMatchDataPolling = ({ type }: UseMatchDataPollingOptions) => {
  const { showInfo, showError } = useSnackbar();

  // Get current workspace ID from workspace store - only for context
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const setUserFilters = useWorkspaceStore((state) => state.setUserFilters);
  const userFilters = useWorkspaceStore((state) => state.userFilters);

  // Subscribe to processingFiles changes for this workspace
  const processingFile = useMatchDataProcessingStore((state) => {
    if (!workspaceId) return null;

    const key = `${workspaceId}_${type}`;
    return (
      state.processingFiles[key] || state.getProcessingFile(workspaceId, type)
    );
  });

  const updateProcessingData = useMatchDataProcessingStore(
    (state) => state.updateProcessingData
  );
  const stopProcessing = useMatchDataProcessingStore(
    (state) => state.stopProcessing
  );

  // Set up polling query if there's a file being processed
  const shouldSkip = !processingFile?.fileId;

  const { stopPolling } = useQuery<CompanyMatchStatusPayload>(
    COMPANY_MATCH_FILE_STATS,
    {
      variables: { fileId: processingFile?.fileId },
      skip: shouldSkip,
      pollInterval: POLLING_INTERVAL_MS,
      notifyOnNetworkStatusChange: true,
      context: { clientName: COMPANY_MATCH_CLIENT },
      fetchPolicy: "cache-and-network", // Fetch from network even if cached
      nextFetchPolicy: "network-only", // Subsequent fetches always from network
      onCompleted: async (data) => {
        if (!data?.stats || !workspaceId) return;

        // Update processing data in store
        updateProcessingData(workspaceId, data.stats);

        // If processing is complete
        const { stats } = data;
        if (stats.lastMatched) {
          try {
            const baseCompanyMatch = {
              fileId: stats.fileId,
              fileName: stats.fileName || "",
              uploaded: stats.uploaded || "",
              lastMatched: stats.lastMatched || null,
              totalRows: stats.totalRows || null,
              processedRows: stats.processedRows || null,
              matchedRows: stats.matchedRows || null,
              invalidRows: stats.invalidRows || null,
              salesWins: stats.salesWins || null,
              salesLosses: stats.salesLosses || null,
              processedChunks: stats.processedChunks || null,
              totalChunks: stats.totalChunks || null,
            };
            if (type === "whitespace") {
              setWorkspace((prev) => ({
                ...(prev as Workspace),
                configuration: {
                  ...(prev as Workspace).configuration,
                  whitespace: {
                    ...baseCompanyMatch,
                    filterMethod: "EXCLUDE_MATCHED" as const,
                  },
                },
              }));
              showInfo(
                "Whitespace processing complete! To persist the changes, please save the workspace."
              );
            } else if (type === "uploadedAccounts") {
              // For uploaded accounts, set the companyMatch in userFilters
              // Use the filter options that were stored when processing started
              setUserFilters({
                ...userFilters,
                companyMatch: {
                  ...baseCompanyMatch,
                  filterMethod: processingFile?.filterMethod || "FILTER",
                  includeHierarchy: processingFile?.includeHierarchy || false,
                },
              });
              showInfo(
                "Uploaded accounts processing complete! To persist the changes, please save the workspace."
              );
            }
          } catch (error) {
            console.error(`Error saving ${type} data:`, error);
            showError(`Error saving ${type} data`);
          } finally {
            stopPolling(); // Stop polling
            stopProcessing(workspaceId, type); // Clean up processing state
          }
        }
      },
      onError: (error) => {
        console.error(`Network/Apollo error polling ${type} status:`, error);
        showError(`Network error checking ${type} status.`);

        // Clean up on error
        stopPolling();
        if (workspaceId) {
          stopProcessing(workspaceId, type);
        }
      },
    }
  );

  // Return relevant functions to be used by components
  return {
    isProcessing: !!processingFile,
    processingData: processingFile,
    stopPolling,
  };
};
